import type { ArchitectureOverview, DeadCodeResult, CommunityData, ADR, CreateADRPayload, UpdateADRPayload } from "../types/pages";
import { api, projectScope } from "./client";

// Raw dead-code shape from backend — groups candidates by module, not a flat array
interface DeadCodeApiResponse {
  total_candidates: number;
  groups: Array<{
    module_path: string;
    candidates: Array<{
      id: string;
      name: string;
      fq_name: string;
      kind: string;
      file_path: string;
      start_line: number | null;
      end_line: number | null;
      confidence: number;
    }>;
  }>;
}

// Raw community shape from backend — field names differ from the CommunityData UI type
interface BackendCommunityResponse {
  cluster_count: number;
  clusters: { community_id: number; size: number; module_paths: string[] }[];
  inter_cluster_edge_density: number;
  error?: string | null;
}

function mapCommunityResponse(res: BackendCommunityResponse): CommunityData {
  const sizes = res.clusters.map((c) => c.size);
  const avg = sizes.length > 0 ? sizes.reduce((a, b) => a + b, 0) / sizes.length : 0;
  return {
    total_clusters: res.cluster_count,
    avg_cluster_size: avg,
    inter_cluster_edge_density: res.inter_cluster_edge_density,
    communities: res.clusters.map((c) => ({
      id: c.community_id,
      node_count: c.size,
      languages: [],
      members: c.module_paths,
    })),
  };
}

// Raw detail shape from backend uses linked_nodes, not linked_symbols
interface ADRDetailApiResponse {
  id: string; title: string; status: string; date: string; content: string;
  linked_nodes: { id: string; name: string; kind: string }[];
}

function mapAdrDetail(res: ADRDetailApiResponse): ADR {
  return {
    id: res.id,
    title: res.title,
    status: res.status as ADR["status"],
    date: res.date,
    content: res.content,
    linked_symbols: res.linked_nodes.map((ln) => ({ id: ln.id, name: ln.name })),
  };
}

export const pagesApi = {
  getArchitecture(repoId: string, projectId?: string): Promise<ArchitectureOverview> {
    return api.get<ArchitectureOverview>(projectScope(`/repos/${repoId}/architecture`, projectId));
  },

  getDeadCode(repoId: string, projectId?: string): Promise<DeadCodeResult[]> {
    return api
      .get<DeadCodeApiResponse>(projectScope(`/repos/${repoId}/dead-code`, projectId))
      .then((res) =>
        res.groups.flatMap((g) =>
          g.candidates.map((c) => ({
            id: c.id,
            name: c.name,
            kind: c.kind,
            file_path: c.file_path,
            language: null,
            confidence: c.confidence,
          }))
        )
      );
  },

  getCommunities(repoId: string, projectId?: string): Promise<CommunityData> {
    return api
      .get<BackendCommunityResponse>(projectScope(`/repos/${repoId}/communities`, projectId))
      .then(mapCommunityResponse);
  },

  getAdrs(repoId: string, projectId?: string): Promise<ADR[]> {
    return api.get<ADR[]>(projectScope(`/repos/${repoId}/adrs`, projectId));
  },

  getAdr(repoId: string, adrId: string, projectId?: string): Promise<ADR> {
    return api
      .get<ADRDetailApiResponse>(projectScope(`/repos/${repoId}/adrs/${adrId}`, projectId))
      .then(mapAdrDetail);
  },

  createAdr(repoId: string, adr: CreateADRPayload, projectId?: string): Promise<ADR> {
    return api
      .post<ADRDetailApiResponse>(projectScope(`/repos/${repoId}/adrs`, projectId), adr)
      .then(mapAdrDetail);
  },

  updateAdr(repoId: string, adrId: string, update: UpdateADRPayload, projectId?: string): Promise<ADR> {
    return api
      .put<ADRDetailApiResponse>(projectScope(`/repos/${repoId}/adrs/${adrId}`, projectId), update)
      .then(mapAdrDetail);
  },
};
